import { useState } from 'react';
import { motion } from 'framer-motion';
import { TreePine, Grid3X3, Download, FileText, Database } from 'lucide-react';
import TreeView from './TreeView';
import MatrixView from './MatrixView';
import { exportToPDF } from '../services/pdf';
import { cn } from '../utils/cn';

function formatDate(date) {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function ResearchResults({ research }) {
  const [activeView, setActiveView] = useState('tree');
  const [exporting, setExporting] = useState(false);

  const handleExport = async (type) => {
    setExporting(true);
    try {
      await exportToPDF(research, type);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with market info */}
      <div className="bg-card rounded-xl p-6 shadow-card">
        {research.fromCache && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3 pb-3 border-b border-border">
            <Database className="w-4 h-4" />
            <span>Loaded from cache (updated {formatDate(research.createdAt)})</span>
          </div>
        )}
        <h2 className="text-title font-display text-primary mb-2">
          {research.marketName}
        </h2>
        <p className="text-muted-foreground mb-4">{research.marketDefinition}</p>
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <span>{research.companies.length} companies</span>
          <span>•</span>
          <span>{research.companies.reduce((sum, c) => sum + c.products.length, 0)} products</span>
          <span>•</span>
          <span>{research.capabilities.length} capabilities</span>
          <span>•</span>
          <span>{research.features.length} features</span>
        </div>
      </div>

      {/* View Toggle and Export */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex rounded-lg bg-muted p-1">
          <button
            onClick={() => setActiveView('tree')}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2",
              activeView === 'tree'
                ? "bg-card shadow-sm text-primary"
                : "text-muted-foreground hover:text-primary"
            )}
          >
            <TreePine className="w-4 h-4" />
            Tree View
          </button>
          <button
            onClick={() => setActiveView('matrix')}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2",
              activeView === 'matrix'
                ? "bg-card shadow-sm text-primary"
                : "text-muted-foreground hover:text-primary"
            )}
          >
            <Grid3X3 className="w-4 h-4" />
            Matrix View
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => handleExport('tree')}
            disabled={exporting}
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium",
              "bg-secondary text-secondary-foreground hover:bg-secondary/90",
              "transition-all flex items-center gap-2 disabled:opacity-50"
            )}
          >
            <FileText className="w-4 h-4" />
            Export Tree PDF
          </button>
          <button
            onClick={() => handleExport('matrix')}
            disabled={exporting}
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium",
              "bg-secondary text-secondary-foreground hover:bg-secondary/90",
              "transition-all flex items-center gap-2 disabled:opacity-50"
            )}
          >
            <Download className="w-4 h-4" />
            Export Matrix PDF
          </button>
        </div>
      </div>

      {/* View Content */}
      <motion.div
        key={activeView}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeView === 'tree' ? (
          <TreeView research={research} />
        ) : (
          <MatrixView research={research} />
        )}
      </motion.div>
    </div>
  );
}
